<template>
  <div class="ibox">
    <div class="ibox-title">
      <h2>Job Setup</h2>
    </div>
    <div class="ibox-content ibox-content-padding-xs">
      <div class="row">
        <div v-if="$user.isAdminOrCoordinator" class="col-lg-3">
          <div class="form-group">
            <label for="" class="control-label">Assigned Coordinator</label>
            <selectize
              v-model="coordinator"
              name="coordinator_id"
              :disabled="jobIsNotEditable"
              :options="coordinatorOptions">
            </selectize>
          </div>
        </div>
        <div class="col-lg-3">
          <div class="form-group">
            <label class="control-label">Total Coordinator Commission</label>
            <div class="input-group">
              <span class="input-group-addon">$</span>
              <p
                class="form-control-static"
                :class="{ 'text-success': coordinatorCommissionIsProfitable, 'text-danger' : coordinatorCommissionIsNotProfitable }">
                {{ coordinatorCommissionAsMoney }}
              </p>
            </div>
          </div>
        </div>
        <div class="col-lg-3">
          <div class="form-group" :class="{ 'has-error': errors.has('public_quantity') }">
            <label class="control-label">Public Job Quantity</label>
            <div class="input-group">
              <input type="number" disabled class="form-control" name="public_quantity" v-model="publicQuantity" />
              <span class="input-group-addon">/tonne</span>
            </div><!-- /.input-group -->
          </div><!-- /.form-group -->
        </div>
        <div v-if="jobIsCompleted" class="col-lg-3 col-lg-offset-3">
          <div class="form-group">
            <label class="control-label">Total Job Commission</label>
            <div class="input-group">
              <span class="input-group-addon">$</span>
              <p class="form-control-static text-success">{{ jobCommissionAsMoney }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="ibox-footer" v-if="jobIsEditable">
      <ladda-button v-if="canCompleteJob" :on-click="handleCompleteJob" class="btn btn-sm btn-primary job-complete">Job Complete</ladda-button>
      <ladda-button v-if="$user.isAdminOrCoordinator" :on-click="handleSaveJobSetup" class="btn btn-sm btn-success pull-right">Save</ladda-button>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapState, mapGetters, mapMutations } from 'vuex'
  import { Selectize, LaddaButton } from 'components/forms'
  import { alert, notify } from 'utils'
  import { Roles } from 'utils/user'
  import { SET_JOB_ATTRIBUTE } from 'vx/mutations'
  import { stateMapper, jobHelpers } from 'mixins/jobs'
  import { filter, concat, map, each, forIn, pick, sortBy, get } from 'lodash'

  import $ from 'jquery'

  export default {
    mixins: [stateMapper, jobHelpers],

    data () {
      return {
        initialUserOptions: []
      }
    },

    components: {
      Selectize,
      LaddaButton
    },

    created () {
      this.getUsers({ master: true, roles: [Roles.COORDINATOR] })
      this.toggleJobCompleteButton(true)
    },

    computed: {
      ...mapGetters('user', ['userOptionsWithNone']),
      ...mapState('user', ['users']),

      canCompleteJob () {
        return this.$user.isAdmin || (this.$user.isCoordinator && this.coordinator !== null)
      },

      coordinatorOptions () {
        const formatCoordinator = coordinator =>
          ({ id: coordinator.id, text: `${coordinator.profile.name} ${coordinator.profile.surname}` })

        return concat([{ id: '', text: '<em>None</em>' }], sortBy(map(this.users, formatCoordinator), 'text'))
      }
    },

    methods: {
      ...mapActions('job', ['getJob', 'updateJob', 'completeJob']),
      ...mapActions('user', ['getUsers']),
      ...mapMutations('job', [SET_JOB_ATTRIBUTE]),

      toggleJobCompleteButton (state) {
        // disable/enable job complete button immediately
        $('.job-complete').prop('disabled', !state)
      },

      /**
       * Handle marking a job as complete.
       *
       * @return Promise
       */
      handleCompleteJob () {
        this.toggleJobCompleteButton(false)

        return this.confirmHaveLoads()
          .then(this.confirmLoadsAreConfirmed)
          .then(this.confirmJobHasRate)
          .then(this.confirmLoadsHaveRate)
          .then(this.confirmLoadsHaveRego)
          .then(this.confirmLoadsHaveApprovedDockets)
          .then(() => this.completeJob(this.job.id))
      },

      /**
       * http://support.codium.com.au/a/tickets/10618
       *
       * All the loads need to have at least one approved dockets
       * unless loads that marks as canceled
       *
       * @return Promise
       */
      confirmLoadsHaveApprovedDockets () {
        const confirmedLoads = filter(this.loads, load => load.status === 'confirmed')

        const confirmedLoadsWithApprovedDockets = filter(confirmedLoads, load =>
          filter(get(load, 'weighbridge.dockets', []), docket => get(docket, 'status') === 'approved').length > 0
        )

        return this.checkIfJobIsAbleToComplete(confirmedLoads.length !== confirmedLoadsWithApprovedDockets.length, {
          title: 'No Approved Dockets',
          text: 'Please add at least a approved docket to confirmed loads before marking a job as complete. ' +
            'Please cancel the load if it has no approved dockets.'
        })
      },

      /**
       * Helper function for checking if a job is able to complete.
       *
       * @param  boolean failed
       * @param  string options.title
       * @param  string options.text
       *
       * @return Promise
       */
      checkIfJobIsAbleToComplete (failed, { title, text }) {
        if (failed) {
          alert.warning({ title, text })

          this.toggleJobCompleteButton(true)
          return Promise.reject()
        }

        return Promise.resolve()
      },

      /**
       * Determine if we have any loads.
       *
       * @return Promise
       */
      confirmHaveLoads () {
        const checked = filter(this.loads, load => load.status !== 'cancelled').length === 0

        return this.checkIfJobIsAbleToComplete(checked, {
          title: 'No Loads',
          text: 'Please add some loads before marking a job as complete.'
        })
      },

      /**
       * Determine if all loads are confirmed.
       *
       * @return Promise
       */
      confirmLoadsAreConfirmed () {
        const invalidLoads = load => load.status !== 'cancelled' && load.status !== 'confirmed'
        const checked = filter(this.loads, invalidLoads).length > 0

        return this.checkIfJobIsAbleToComplete(checked, {
          title: 'Unconfirmed Loads',
          text: 'Please confirm all loads before marking a job as complete.'
        })
      },

      confirmJobHasRate () {
        const checked = this.job.rate === null || this.job.rate < 0

        return this.checkIfJobIsAbleToComplete(checked, {
          title: 'Job Rate Missing',
          text: 'Please make sure the job has a rate.'
        })
      },

      confirmLoadsHaveRate () {
        const invalidLoads = load => load.status !== 'cancelled' && (load.rate === null || !$.isNumeric(load.rate) || load.rate < 0)
        const checked = filter(this.loads, invalidLoads).length > 0

        return this.checkIfJobIsAbleToComplete(checked, {
          title: 'Load Rate Missing',
          text: 'Please make sure all loads have load rates.'
        })
      },

      /**
       * http://support.codium.com.au/a/tickets/9827
       * Make sure all the loads have rego
       *
       * @return Promise
       */
      confirmLoadsHaveRego () {
        const invalidLoads = load => load.status !== 'cancelled' && !(load.truck && load.truck.registration)
        const checked = filter(this.loads, invalidLoads).length > 0

        return this.checkIfJobIsAbleToComplete(checked, {
          title: 'Load Rego Missing',
          text: 'Please make sure all loads have rego.'
        })
      },

      /**
       * Handle saving the job setup.
       *
       * @return Promise
       */
      handleSaveJobSetup () {
        let payload = pick(this.job, [
          'user_id',
          'coordinator_id',
          'public_quantity'
        ])

        return this.updateJob({ id: this.job.id, payload: payload })
        .then(() => {
          notify.success('Job Saved')
          this.errors.clear()
        })
        .catch(exception => {
          if (exception.response && exception.response.status === 422) {
            forIn(exception.response.data.errors, (errors, key) => {
              each(errors, error => {
                this.errors.add(key, error)
                notify.error(error)
              })
            })
          }
        })
      }
    }
  }
</script>



// WEBPACK FOOTER //
// JobSetup.vue?673df1a4